import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { useHealthAndSafetyService } from '../../../../apis/healthAndSafety/useHealthAndSafetyService';
import {
  TSafetyProtocol,
  TSafetyProtocolListApiResponse,
  TSafetyProtocolParams,
} from '../../../../apis/healthAndSafety/healthAndSafety.api.interfaces';

type TQueryError = IFHError<{ message: string }>;
type TMutationError = IFHError<{ message: string }>;

// Query keys
export const getSafetyProtocolListKey = 'get-safety-protocol-list';
export const getSafetyProtocolByIdKey = 'get-safety-protocol-by-id';

// ===================== LIST HOOK =====================
export const useSafetyProtocolListHook = (
  params: TSafetyProtocolParams,
  options?: UseQueryOptions<TSafetyProtocolListApiResponse, TQueryError>
): UseQueryResult<TSafetyProtocolListApiResponse, TQueryError> => {
  const { getSafetyProtocolList } = useHealthAndSafetyService();

  return useQuery<TSafetyProtocolListApiResponse, TQueryError>({
    queryKey: [getSafetyProtocolListKey, params],
    queryFn: () => getSafetyProtocolList(params),
    enabled: true,
    ...options,
  });
};

// ===================== GET BY ID HOOK =====================
export const useSafetyProtocolByIdHook = (
  id: string,
  options?: UseQueryOptions<TSafetyProtocol, TQueryError>
): UseQueryResult<TSafetyProtocol, TQueryError> => {
  const { getSafetyProtocolById } = useHealthAndSafetyService();

  return useQuery<TSafetyProtocol, TQueryError>({
    queryKey: [getSafetyProtocolByIdKey, id],
    queryFn: () => getSafetyProtocolById(id),
    enabled: Boolean(id),
    ...options,
  });
};

// ===================== CREATE HOOK =====================
export const useCreateSafetyProtocolHook = (
  options?: UseMutationOptions<
    TSafetyProtocol,
    TMutationError,
    Omit<TSafetyProtocol, 'id' | 'createdAt' | 'updatedAt'>
  >
): UseMutationResult<
  TSafetyProtocol,
  TMutationError,
  Omit<TSafetyProtocol, 'id' | 'createdAt' | 'updatedAt'>
> => {
  const { createSafetyProtocol } = useHealthAndSafetyService();
  const queryClient = useQueryClient();

  return useMutation<
    TSafetyProtocol,
    TMutationError,
    Omit<TSafetyProtocol, 'id' | 'createdAt' | 'updatedAt'>
  >({
    mutationFn: (protocol) => createSafetyProtocol(protocol),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getSafetyProtocolListKey] });
    },
    ...options,
  });
};

// ===================== UPDATE HOOK =====================
export const useUpdateSafetyProtocolHook = (
  options?: UseMutationOptions<
    TSafetyProtocol,
    TMutationError,
    { id: string; data: Partial<TSafetyProtocol> }
  >
): UseMutationResult<
  TSafetyProtocol,
  TMutationError,
  { id: string; data: Partial<TSafetyProtocol> }
> => {
  const { updateSafetyProtocol } = useHealthAndSafetyService();
  const queryClient = useQueryClient();

  return useMutation<
    TSafetyProtocol,
    TMutationError,
    { id: string; data: Partial<TSafetyProtocol> }
  >({
    mutationFn: ({ id, data }) => updateSafetyProtocol(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [getSafetyProtocolListKey] });
      queryClient.invalidateQueries({
        queryKey: [getSafetyProtocolByIdKey, variables.id],
      });
    },
    ...options,
  });
};

// ===================== DELETE HOOK =====================
export const useDeleteSafetyProtocolHook = (
  options?: UseMutationOptions<void, TMutationError, string>
): UseMutationResult<void, TMutationError, string> => {
  const { deleteSafetyProtocol } = useHealthAndSafetyService();
  const queryClient = useQueryClient();

  return useMutation<void, TMutationError, string>({
    mutationFn: (id) => deleteSafetyProtocol(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getSafetyProtocolListKey] });
    },
    ...options,
  });
};
